import { competitorMentionPrefilter, OWN_CHANNEL } from "./competitorIntel";
import { extractVerifierMoney, hasFigure, unreadableVerifierFigures } from "./figures";
import { foldApostrophes, SCENARIO_MAP, type Scenario } from "./scenario";
import type { WindowState } from "./window";

/**
 * TA-1336 item C, the source question. Kabir typed on 2026-10-14 that the shop should learn, through DMs and
 * "more descretely, like 'hey, whered u get this from' what r u looking for etc.", where customers saw a piece
 * (competitive pricing intelligence), and approved the recommendations ("go ahead build"): the REPLY path asks,
 * not the analyst; at most one natural question per conversation; only "where", never "how much did they quote
 * you" (that invites a price match, which CLAUDE.md section 0.5 forbids); never on class D, never in the grace
 * window's contact capture, never when unsure.
 *
 * It is a fixed line code appends, modelled on the contact ask (`contactAsk.ts`): a model never writes it, so
 * the words are reviewed once and registered (`outboundText.ts`, `source_question.appended`). It reads as a
 * question a good salesperson asks anyway, never covert (B14). BUILT OFF: `ACKRA_ENABLE_SOURCE_QUESTION` is
 * unset by default and turning it on waits for a paid eval run on Kabir's typed go (section 11).
 *
 * Silence is a first-class action (section 7): every refusal below is a return value, and the caller adds
 * nothing and logs nothing beyond what already exists.
 */

/**
 * AWAITING RATIFICATION: the wording is the implementer's, from Kabir's brief. No figure, no offer word, no
 * mention of price or of a competitor.
 */
export const SOURCE_QUESTION_LINE = "Out of curiosity, where did you first come across it?";

/**
 * The scenarios whose reply may carry the question: an enquiry about a specific piece the customer saw.
 * `ready_made_purchase` is the product, availability and price enquiry row. Left out on purpose, "when in doubt
 * leave it out": `price_challenge` (the customer is already comparing, and asking where invites a price match),
 * `custom_engagement` (a commission, not a piece seen somewhere), `educated_buyer_debate`, `repair_inquiry`,
 * `post_purchase_care`, `consult_confirm`, `review_ask`, `ghost_revival`, `vendor_spam`, and every class D row.
 */
export const SOURCE_QUESTION_SCENARIOS: readonly Scenario[] = Object.freeze(["ready_made_purchase"] as const);

/** The fixed question after a composed reply, as its own sentence. Same joining as `appendContactAsk`. */
export function appendSourceQuestion(reply: string): string {
  const body = reply.trimEnd();
  const ended = /[.!?\p{Extended_Pictographic}\p{Emoji_Modifier}\p{M}]$/u.test(body);
  return `${body}${ended ? "" : "."} ${SOURCE_QUESTION_LINE}`;
}

/**
 * A customer who already says where they came across the piece, or who named a person or place that sent them.
 * Deliberately wide: a false hit only loses a question (precision beats recall). Local to this module; another
 * agent may build a shared prefilter.
 */
const SAID_WHERE: readonly RegExp[] = [
  /\b(?:saw|seen|spotted|found|discovered|noticed|came across|stumbled (?:on|upon|across)|read about|heard about|heard of)\b[^.!?\n]{0,60}\b(?:on|in|at|from|via|through|by|online|instagram|insta|ig|tiktok|facebook|fb|pinterest|google|website|site|story|stories|post|reel|ad|magazine|page|feed)\b/i,
  OWN_CHANNEL,
  /\b(?:friend|family|sister|brother|mom|mother|dad|father|husband|wife|partner|fianc[eé]e?|coworker|colleague|someone|person)\b[^.!?\n]{0,40}\b(?:showed|told|sent|recommended|referred|mentioned|shared|pointed)\b/i,
  /\b(?:showed|told|sent|recommended|referred|mentioned|shared|pointed)\s+(?:me|us)\b[^.!?\n]{0,40}\b(?:to|this|that|it|you|your|ur)\b/i,
  /\b(?:i|we)\s+(?:follow|followed|follows)\s+(?:you|ur|your)\b/i,
  /\b(?:referral|referred)\b/i,
];

export function customerSaidWhere(customerTurns: readonly string[]): boolean {
  return customerTurns.some((t) => {
    const text = foldApostrophes(t);
    return SAID_WHERE.some((p) => p.test(text));
  });
}

export interface SourceQuestionInput {
  /** `ACKRA_ENABLE_SOURCE_QUESTION` is exactly "1". */
  readonly enabled: boolean;
  /** The scenario the router chose for this turn. */
  readonly scenario: Scenario;
  /** The window gate's state, or "not_applicable" off Meta's 24h rails. */
  readonly window: WindowState | "not_applicable";
  /** The composed draft's goal_step. */
  readonly goalStep: string;
  /** The composed draft's reply, before the question. */
  readonly reply: string;
  /** The customer's turns: the thread window's and the message being answered. */
  readonly customerTurns: readonly string[];
  /** A `source.asked` event already exists on this thread. */
  readonly askedBefore: boolean;
}

export type SourceQuestionRefusal =
  | "flag_off"
  | "class_d"
  | "scenario"
  | "window"
  | "goal_step"
  | "empty_reply"
  | "draft_has_question"
  | "reply_has_figure"
  | "customer_said_where"
  | "competitor_mentioned"
  | "asked_before";

/**
 * Whether code appends the source question to a composed reply. Pure. Every check can only refuse. Class D is
 * read from the scenario map (T3, section 3), not from a copy of its row names.
 */
export function sourceQuestionDecision(
  i: SourceQuestionInput,
): { readonly append: true } | { readonly append: false; readonly reason: SourceQuestionRefusal } {
  const no = (reason: SourceQuestionRefusal) => ({ append: false as const, reason });
  if (!i.enabled) return no("flag_off");
  if (SCENARIO_MAP[i.scenario].graduationClass === "D") return no("class_d");
  if (!SOURCE_QUESTION_SCENARIOS.includes(i.scenario)) return no("scenario");
  // Contact capture owns grace (F-01, F-02); nothing is asked outside the window, or where it does not gate.
  if (i.window !== "in_window") return no("window");
  if (i.goalStep !== "answer") return no("goal_step");
  if (i.reply.trim() === "") return no("empty_reply");
  // One question per message: any question mark, ASCII, fullwidth or Arabic, is a question already.
  if (/[?？؟;]/.test(i.reply)) return no("draft_has_question");
  // TA-1336 review: never right after a figure. "Where did you come across it?" under a quoted price reads as
  // "where else did you see that price", which invites a price match (section 0.5). Any figure the shared
  // reader finds (F-16), money the verifier reads, or money it refuses to read whole, is a figure.
  if (hasFigure(i.reply) || extractVerifierMoney(i.reply).length > 0 || unreadableVerifierFigures(i.reply).length > 0) {
    return no("reply_has_figure");
  }
  if (customerSaidWhere(i.customerTurns)) return no("customer_said_where");
  // TA-1336 review: a customer who already brought up another shop or site is comparing; asking where then
  // invites a price match. The prefilter that sends a message to the competitor read decides it.
  if (i.customerTurns.some((t) => competitorMentionPrefilter(t))) return no("competitor_mentioned");
  if (i.askedBefore) return no("asked_before");
  return { append: true };
}
